import {
  ArrowDownward,
  ArrowUpward,
  FilterAlt,
  MoreVert,
  SyncAlt,
} from "@mui/icons-material";
import { useTheme } from "@mui/material";
import { MRT_TableOptions } from "material-react-table";
import { useMemo } from "react";
import {
  AUTURA_ASPHALT,
  AUTURA_ASPHALT_DARK,
  AUTURA_GRAYSCALE,
  TEXT_PRIMARY,
} from "context/themeContext";
import { CalendarIcon, ClearIcon } from "@mui/x-date-pickers";

export enum TableType {
  PANEL = "panel",
  NESTED = "nested",
  SIMPLE = "simple",
}

export const useDefaultTableOptions = <T extends object>(
  tableType: TableType = TableType.PANEL,
): Partial<MRT_TableOptions<T>> => {
  const { palette } = useTheme();

  const defaults = useMemo<Partial<MRT_TableOptions<T>>>(() => {
    switch (tableType) {
      case TableType.PANEL:
        return {
          enableRowActions: true,
          enableTopToolbar: true,
          enableTableFooter: false,
          enableBottomToolbar: false,
          enableStickyFooter: false,
          enablePagination: false,
          enableStickyHeader: true,
          enableColumnOrdering: false,
          enableDensityToggle: false,
          enableRowVirtualization: false,
          enableFullScreenToggle: true,
          enableRowSelection: false,
          enableExpandAll: false,
          icons: {
            // only icons which need to be overridden
            SyncAltIcon: (props: object) => (
              <SyncAlt
                {...props}
                htmlColor={AUTURA_GRAYSCALE[100]}
                className="custom-header-icon"
              />
            ),
            MoreVertIcon: (props: object) => (
              <MoreVert
                {...props}
                sx={{ color: AUTURA_GRAYSCALE[100] }}
                className="custom-header-icon"
              />
            ),
            FilterAltIcon: (props: object) => (
              <FilterAlt
                {...props}
                sx={{ color: AUTURA_GRAYSCALE[100] }}
                className="custom-header-icon"
              />
            ),
            ArrowDownwardIcon: (props: any) => {
              const desc = props.className.includes(
                "MuiTableSortLabel-iconDirectionDesc",
              );
              return desc ? (
                <ArrowDownward
                  {...props}
                  sx={{ color: AUTURA_GRAYSCALE[100] }}
                  className="custom-header-icon"
                />
              ) : (
                <ArrowUpward
                  {...props}
                  sx={{ color: AUTURA_GRAYSCALE[100] }}
                  className="custom-header-icon"
                />
              );
            },
          },
          muiTablePaperProps: () => ({
            sx: {
              position: "relative", // Container for absolute positioning
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: "transparent",
              borderRadius: 0,
            },
          }),
          muiTableContainerProps: () => ({
            sx: {
              flex: 1,
              pt: "70px",
              display: "flex",
              flexDirection: "column",
              backgroundColor: palette.secondary.main,
              maxHeight: `calc(100dvh - 100px - 70px - 32px + 50px)`,
            },
          }),
          muiTableBodyProps: () => ({
            sx: {
              backgroundColor: "white",
              width: "100%",
            },
          }),
          muiSelectAllCheckboxProps: {
            sx: {
              color: AUTURA_GRAYSCALE[100],
            },
          },
          muiFilterTextFieldProps: {
            size: "small",
            variant: "standard",
            margin: "none",
            sx: {
              color: AUTURA_GRAYSCALE[100],
            },
            InputProps: {
              sx: {
                color: AUTURA_GRAYSCALE[100],
                borderBottom: "1px solid",
                borderBottomColor: AUTURA_GRAYSCALE[100],
                "&:hover": {
                  borderBottomColor: AUTURA_GRAYSCALE[100],
                },
              },
            },
            slotProps: {
              input: {
                sx: {
                  color: AUTURA_GRAYSCALE[100],
                  borderBottom: "1px solid",
                  borderBottomColor: AUTURA_GRAYSCALE[100],
                  "&:hover": {
                    borderBottomColor: AUTURA_GRAYSCALE[100],
                  },
                },
              },
            },
          },
          muiColumnDragHandleProps: {
            sx: {
              color: AUTURA_GRAYSCALE[100],
            },
          },
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: AUTURA_GRAYSCALE[500],
              color: "white",
              fontWeight: "bold",
              cursor: "pointer",
              pl: 2,
            },
          },
          muiTableHeadRowProps: {
            sx: {
              backgroundColor: AUTURA_GRAYSCALE[500],
              color: "white",
              fontWeight: "bold",
              cursor: "pointer",
            },
          },
          muiTableBodyCellProps: {
            sx: {
              typography: "body2",
              backgroundColor: "white",
              pl: 2,
            },
          },
          muiColumnActionsButtonProps: {
            sx: {
              color: palette.primary.contrastText,
            },
          },
          muiSearchTextFieldProps: {
            size: "small",
            margin: "none",
            InputProps: {
              sx: {
                backgroundColor: "white",
                border: `1px solid ${AUTURA_ASPHALT_DARK}`,
              },
            },
          },
          muiFilterDatePickerProps: {
            slots: {
              clearIcon: (props: object) => (
                <ClearIcon
                  {...props}
                  sx={{ color: AUTURA_GRAYSCALE[100] }}
                  className="custom-header-icon"
                />
              ),
              openPickerIcon: (props: object) => (
                <CalendarIcon
                  {...props}
                  sx={{ color: AUTURA_GRAYSCALE[100] }}
                  className="custom-header-icon"
                />
              ),
            },
          },
        };
      case TableType.NESTED:
        return {
          enableRowActions: true,
          enableTopToolbar: true,
          enableTableFooter: false,
          enableBottomToolbar: false,
          enableStickyFooter: false,
          enableColumnActions: false,
          enablePagination: false,
          enableStickyHeader: true,
          enableColumnOrdering: false,
          enableDensityToggle: false,
          enableRowVirtualization: false,
          enableFullScreenToggle: true,
          enableRowSelection: false,
          enableExpandAll: false,
          muiTablePaperProps: () => ({
            elevation: 4,
            sx: {
              border: `2px solid ${AUTURA_ASPHALT_DARK}`,
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: "transparent",
              borderRadius: 1,
            },
          }),
          muiTableContainerProps: () => ({
            sx: {
              flex: 1,
              display: "flex",
              flexDirection: "column",
              maxHeight: `calc(100dvh - 100px - 70px - 32px - 252px)`,
              minHeight: `calc(100dvh - 100px - 70px - 32px - 252px)`,
            },
          }),
          muiTableBodyProps: () => ({
            sx: {
              backgroundColor: "white",
              width: "100%",
            },
          }),
          muiSelectAllCheckboxProps: {
            sx: {
              color: TEXT_PRIMARY,
            },
          },
          muiFilterTextFieldProps: {
            size: "small",
            variant: "standard",
            margin: "none",
            sx: {
              color: TEXT_PRIMARY,
            },
            InputProps: {
              sx: {
                color: "white",
                borderBottom: "1px solid",
                borderBottomColor: AUTURA_GRAYSCALE[100],
                "&:hover": {
                  borderBottomColor: AUTURA_GRAYSCALE[100],
                },
              },
            },
          },
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: AUTURA_GRAYSCALE[200],
              color: TEXT_PRIMARY,
              fontWeight: "bold",
              cursor: "pointer",
              pl: 2,
              borderBottom: `2px solid ${palette.primary.main}`,
            },
          },
          muiTableBodyCellProps: {
            sx: {
              typography: "body2",
              backgroundColor: "white",
              pl: 2,
            },
          },
          muiSearchTextFieldProps: {
            size: "small",
            margin: "none",
            InputProps: {
              sx: {
                backgroundColor: "white",
                border: `1px solid ${AUTURA_ASPHALT}`,
              },
            },
          },
        };
      case TableType.SIMPLE:
        // Simple table needs to provide it's own container props (maxHeight, etc)
        return {
          enableRowActions: false,
          enableTopToolbar: false,
          enableTableFooter: false,
          enableBottomToolbar: false,
          enableStickyFooter: false,
          enableStickyHeader: false,
          enableColumnOrdering: false,
          enableDensityToggle: false,
          enableRowVirtualization: false,
          enablePagination: false,
          enableColumnActions: false,
          muiTablePaperProps: () => ({
            elevation: 4,
            sx: {
              border: `2px solid ${AUTURA_ASPHALT_DARK}`,
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: "transparent",
              borderRadius: 1,
            },
          }),
          muiTableBodyProps: () => ({
            sx: {
              backgroundColor: "white",
              width: "100%",
              height: "100%",
            },
          }),
          muiTableHeadCellProps: {
            sx: {
              backgroundColor: AUTURA_GRAYSCALE[200],
              color: TEXT_PRIMARY,
              fontWeight: "bold",
              cursor: "pointer",
              pl: 2,
              borderBottom: `2px solid ${palette.primary.main}`,
            },
          },
          muiTableBodyCellProps: {
            sx: {
              typography: "body2",
              backgroundColor: "white",
              pl: 2,
            },
          },
        };
      default:
        return {};
    }
  }, [tableType]);

  return defaults as Partial<MRT_TableOptions<T>>;
};
